import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Col,
  Collapse,
  Drawer,
  Dropdown,
  Row,
  Space,
  type MenuProps,
} from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { CrossAnalysisComparison } from './CrossAnalysisComparison';

type Props = {
  icon: ReactNode;
  label: string;
  extra?: ReactNode;
};

type Attribute = keyof typeof attributes;

type MenuItem = Exclude<MenuProps['items'], undefined>[number];

const attributes = {
  ageGroup: 'age_group',
  gender: 'gender',
} as const;

const CrossAnalysisPanel = ({
  icon,
  label,
  extra,
  ...props
}: Props): ReactNode => {
  return (
    <Collapse.Panel
      key="cross_analysis"
      header={
        <Space>
          {icon}
          {label}
          {extra}
        </Space>
      }
      {...props}
    >
      <CrossAnalysisPanelContent />
    </Collapse.Panel>
  );
};

const CrossAnalysisPanelContent = (): ReactNode => {
  const [isOpen, setIsOpen] = useState(false);
  const [firstAttribute, setFirstAttribute] = useState<Attribute>('ageGroup');
  const [secondAttribute, setSecondAttribute] = useState<Attribute>('gender');

  const { t } = useTranslation();

  return (
    <>
      <Row wrap={false} gutter={16}>
        <Col
          span={18}
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          <Dropdown
            menu={{
              items: Object.keys(attributes)
                .filter((key): key is Attribute => {
                  return key !== firstAttribute;
                })
                .map((key: Attribute): MenuItem => {
                  return {
                    key: key,
                    label: t(key),
                    onClick: (): void => {
                      setFirstAttribute(key);
                      if (secondAttribute === key) {
                        setSecondAttribute(firstAttribute);
                      }
                    },
                  };
                }),
            }}
            trigger={['click']}
          >
            <Button type="ghost">
              {t(firstAttribute)}
              <DownOutlined />
            </Button>
          </Dropdown>
          <Space>
            <FontAwesomeIcon
              icon={faTimes}
              color="var(--primary-color)"
              style={{
                fontSize: '18px',
                width: '24px',
                transform: 'translateY(1px)',
              }}
            />
          </Space>
          <Dropdown
            menu={{
              items: Object.keys(attributes)
                .filter((key): key is Attribute => {
                  return key !== secondAttribute;
                })
                .map((key: Attribute) => {
                  return {
                    key: key,
                    label: t(key),
                    onClick: (): void => {
                      setSecondAttribute(key);
                      if (firstAttribute === key) {
                        setFirstAttribute(secondAttribute);
                      }
                    },
                  };
                }),
            }}
            trigger={['click']}
          >
            <Button type="ghost">
              {t(secondAttribute)}
              <DownOutlined />
            </Button>
          </Dropdown>
        </Col>
        <Col
          span={6}
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
          }}
        >
          <Button
            onClick={(): void => {
              setIsOpen(true);
            }}
          >
            {t('display')}
          </Button>
        </Col>
      </Row>
      <Drawer
        title={t('crossAnalysis')}
        placement="right"
        width="80%"
        destroyOnClose
        open={isOpen}
        onClose={(): void => {
          setIsOpen(false);
        }}
      >
        <CrossAnalysisComparison
          title={`${t(firstAttribute)} × ${t(secondAttribute)} ${t(
            'distribution'
          )}`}
          noDataMessage={t('message.noAgentFound')}
        />
      </Drawer>
    </>
  );
};

export { CrossAnalysisPanel };
